import { useState } from 'react';
import {
  ChevronDown,
  LogOut,
  MessageSquare,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FeedbackModal } from '@/components/FeedbackModal';
import { useSession } from '@/hooks/useSession';

/**
 * Account avatar + dropdown for the signed-in workspace.
 *
 * Shared across the list pages (`AppHeader`) and the focused workspaces
 * (Editor / NewDraft) so the user always has a consistent way to view
 * which account they're on, jump to Settings, or sign out — even after
 * they've left the main app shell.
 */
export function UserMenu() {
  const { user } = useSession();
  const email = user?.email ?? null;
  const initial = email?.[0]?.toUpperCase() ?? '?';
  const image = user?.image ?? null;
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex items-center gap-2 rounded-full p-1 pr-2 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          aria-label="Account menu"
        >
          <Avatar className="size-7">
            {image ? <AvatarImage src={image} alt="" /> : null}
            <AvatarFallback className="text-xs">{initial}</AvatarFallback>
          </Avatar>
          <ChevronDown className="size-3.5" aria-hidden />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-60">
          {email ? (
            <>
              <DropdownMenuLabel className="flex flex-col gap-0.5 py-2 font-normal">
                <span className="text-muted-foreground text-[10px] uppercase tracking-wider">
                  Signed in as
                </span>
                <span className="text-foreground truncate text-sm font-medium">{email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          ) : null}

          <DropdownMenuItem asChild>
            <Link to="/app/settings" className="cursor-pointer">
              <SettingsIcon className="size-4" />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setFeedbackOpen(true);
            }}
            className="cursor-pointer"
          >
            <MessageSquare className="size-4" />
            Send feedback
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <a href="/sign-out" className="cursor-pointer">
              <LogOut className="size-4" />
              Sign out
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <FeedbackModal
        open={feedbackOpen}
        defaultCategory="other"
        onClose={() => setFeedbackOpen(false)}
      />
    </>
  );
}
